import React from 'react';
import { Box, Card, IconButton, Tooltip, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Timestamp } from 'firebase/firestore';
import { Comic, FORMAT_LABELS, isRead, isStarted, nextReadLabel } from '../types';
import { cardFoot, footPink, footSecondary } from '../../../utils/surfaces';
import { brand } from '../../../theme/colors';
import CoverImage from './CoverImage';
import ReadStateIcon from './ReadStateIcon';
import { DragHandle, sameHandle, useSortableCard } from './useSortableCard';

interface ComicCardProps {
  comic: Comic;
  // "Batman (2016) #9", or "Standalone"
  subtitle: string;
  isReadOnly: boolean;
  // Wishlist in My order, signed in
  draggable?: boolean;
  onOpen?: (comic: Comic) => void;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, comic: Comic) => void;
  onToggleRead: (comic: Comic) => void;
  onToggleOwned: (comic: Comic) => void;
  onToggleFavourite: (comic: Comic) => void;
}

const overlayButton = {
  position: 'absolute',
  top: 4,
  bgcolor: 'rgba(0,0,0,0.55)',
  color: 'common.white',
  backdropFilter: 'blur(4px)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
} as const;

// "24 Sep" this year, "Sep 2025" before: fits beside the buttons
const shortDate = (ts?: Timestamp | null) => {
  if (!ts) return '';
  const date = ts.toDate();
  return date.getFullYear() === new Date().getFullYear()
    ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    : date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

type CardContentProps = ComicCardProps & { handle: DragHandle };

// Everything inside a card, apart from its drag wrapper (see useSortableCard).
const CardContentView = ({
  comic,
  subtitle,
  isReadOnly,
  draggable = false,
  onOpen,
  onMenuClick,
  onToggleRead,
  onToggleOwned,
  onToggleFavourite,
  handle,
}: CardContentProps) => {
  const { attributes, listeners, setActivatorNodeRef } = handle;

  const FavouriteIcon = comic.favourite ? StarIcon : StarBorderIcon;
  const read = isRead(comic);
  const started = isStarted(comic);
  const readLabel = nextReadLabel(comic.readStatus);
  const OwnedIcon = comic.owned ? MenuBookIcon : MenuBookOutlinedIcon;
  const ownedLabel = comic.owned ? 'Mark not owned' : 'Mark owned';

  return (
    <>
      <CoverImage
        src={comic.coverUrl}
        title={comic.title}
        onClick={onOpen ? () => onOpen(comic) : undefined}
      />

      {draggable && (
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          aria-label={`Drag ${comic.title}`}
          {...attributes}
          {...listeners}
          sx={{
            ...overlayButton,
            left: 4,
            cursor: 'grab',
            touchAction: 'none',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: draggable ? 42 : 8,
          px: 1,
          py: 0.25,
          borderRadius: 999,
          bgcolor: 'rgba(0,0,0,0.6)',
          color: 'common.white',
          fontSize: 11,
          fontWeight: 800,
          pointerEvents: 'none',
        }}
      >
        {FORMAT_LABELS[comic.format]}
      </Box>

      {!isReadOnly && (
        <IconButton
          size="small"
          aria-label={`${comic.title} options`}
          onClick={(e) => onMenuClick(e, comic)}
          sx={{ ...overlayButton, right: 4 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}

      <Box
        sx={(theme) => ({
          p: 1.25,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          ...cardFoot(theme),
        })}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {comic.title}
          </Typography>
          <Typography
            variant="caption"
            component="div"
            sx={(theme) => ({
              color: footSecondary(theme),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            })}
          >
            {subtitle}
          </Typography>
        </Box>

        {/* The eye marks it read or unread; read comics also show their stats */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}>
          {isReadOnly ? (
            <ReadStateIcon
              status={comic.readStatus}
              aria-label={read ? 'Read' : started ? 'Started' : 'Not read'}
              sx={(theme) => ({ fontSize: 20, color: read || started ? 'primary.main' : footSecondary(theme) })}
            />
          ) : (
            <Tooltip title={readLabel}>
              <IconButton
                size="small"
                aria-label={`${readLabel}: ${comic.title}`}
                aria-pressed={read ? true : started ? 'mixed' : false}
                onClick={() => onToggleRead(comic)}
                sx={(theme) => ({ p: 0.25, color: read || started ? 'primary.main' : footSecondary(theme) })}
              >
                <ReadStateIcon status={comic.readStatus} sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Owned sits beside the eye; visitors only see it when owned */}
          {isReadOnly ? (
            comic.owned && <MenuBookIcon aria-label="Owned" sx={{ fontSize: 19, color: brand.pink }} />
          ) : (
            <Tooltip title={ownedLabel}>
              <IconButton
                size="small"
                aria-label={`${ownedLabel}: ${comic.title}`}
                aria-pressed={comic.owned}
                onClick={() => onToggleOwned(comic)}
                sx={(theme) => ({ p: 0.25, color: comic.owned ? brand.pink : footSecondary(theme) })}
              >
                <OwnedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}

          {read ? (
            <>
              {comic.readCount > 1 && (
                <Tooltip title={`Read ${comic.readCount}×`}>
                  <Typography
                    variant="caption"
                    sx={(theme) => ({ color: footSecondary(theme), fontWeight: 700 })}
                  >
                    {comic.readCount}×
                  </Typography>
                </Tooltip>
              )}

              {isReadOnly ? (
                comic.favourite && <FavouriteIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              ) : (
                <Tooltip title={comic.favourite ? 'Remove favourite' : 'Mark favourite'}>
                  <IconButton
                    size="small"
                    aria-label={comic.favourite ? 'Remove favourite' : 'Mark favourite'}
                    onClick={() => onToggleFavourite(comic)}
                    sx={{ p: 0, color: comic.favourite ? 'primary.main' : 'text.disabled' }}
                  >
                    <FavouriteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

              <Typography
                variant="caption"
                noWrap
                title={comic.lastReadAt ? comic.lastReadAt.toDate().toLocaleDateString() : undefined}
                sx={(theme) => ({ ml: 'auto', color: footSecondary(theme) })}
              >
                {shortDate(comic.lastReadAt)}
              </Typography>
            </>
          ) : started ? (
            <Typography
              variant="caption"
              sx={(theme) => ({ ml: 'auto', color: footPink(theme), fontWeight: 700 })}
            >
              Started
            </Typography>
          ) : (
            <Typography variant="caption" sx={(theme) => ({ ml: 'auto', color: footSecondary(theme) })}>
              Not read
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

// Callbacks are stable from the page, so the rest compares shallowly
const CardContent = React.memo(CardContentView, (prev, next) => {
  const { handle: prevHandle, ...prevRest } = prev;
  const { handle: nextHandle, ...nextRest } = next;
  return (
    sameHandle(prevHandle, nextHandle) &&
    (Object.keys(prevRest) as (keyof typeof prevRest)[]).every((key) => prevRest[key] === nextRest[key])
  );
});

const cardSx = {
  borderRadius: 2,
  overflow: 'hidden',
  position: 'relative',
  border: '1px solid',
  borderColor: 'primary.main',
} as const;

// A comic's cover card: the drag wrapper around its content
const ComicCard = (props: ComicCardProps) => {
  const { setNodeRef, style, handle } = useSortableCard(props.comic.id, !props.draggable);
  return (
    <Card ref={setNodeRef} sx={cardSx} style={style}>
      <CardContent {...props} handle={handle} />
    </Card>
  );
};

export default ComicCard;
